import React, { useContext, useCallback, useState, useMemo } from 'react';
import { IEditorProps } from './Editor';
import { IPropertyChild } from 'components/property-editors/utils/ProviderNodeUtils';
import { ContextListPropertyNode } from '@decisions/server/DecisionsFramework/Design/Properties/ContextListPropertyNode';
import { EditorConfigButton } from './EditorConfigButton';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { ListBoxEmptyMessage } from '../lists/ListBoxEmptyMessage';
import { IListItemProps } from '../lists/ListItem';
import { EditorConfigButtons } from './EditorConfigButtons';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { EditorLabel } from './EditorLabel';
import { EditorValidationWrapper } from './EditorValidationWrapper';
import { ArraySortOrder } from '@decisions/server/DecisionsFramework/Design/Properties/ArraySortOrder';
import { ArrayListItem } from './ArrayListItem';
import { isNullOrWhitespace } from 'utils/StringUtils';
import {
  moveValueUp,
  moveValueDown,
  removeValue,
  removeCommand,
} from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderService';
import { ContextProviderNode, ObjectSelectionOptions } from '@decisions/server/DecisionsFramework/Design/Properties';
import classNames from 'classnames';
import './ArrayEditor.less';
import { ActionsProps } from './ArrayListItem';
import {
  addValueAndOpenEditor,
  editValueAndOpenEditor,
} from '@decisions/server/DecisionsFramework/Design/Properties/PropertyEditorService';
import { PropertyGridDialog } from '../Dialogs/PropertyGridDialog';

interface ListItemComparator {
  (a: IListItemProps, b: IListItemProps): number;
}

export interface IArrayEditorProps extends IEditorProps {
  /** Allow providing custom sort handler */
  compareFunction?: ListItemComparator;
  actions?: ActionsProps[];
  containerClassName?: string;
  /** Scenarios where we want to handle Remove action differently OR we want to hide the remove icon */
  disableRemoveAction?: boolean;
}

const DIALOG_SIZE = { height: 700, width: 600 };

/**
 * Array editor component
 *
 * TODO i18n of buttons
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export function ArrayEditor({
  child,
  compareFunction,
  path,
  actions,
  containerClassName,
  disableRemoveAction,
}: IArrayEditorProps) {
  const [busyIndex, setBusyIndex] = useState(-1);
  const [dialogInfo, setDialogInfo] = useState<{
    open: boolean;
    snapshot: ContextProviderNode | undefined;
    title: string;
  }>({
    open: false,
    snapshot: undefined,
    title: '',
  });

  const { editingContextId, onChange, refreshContext, refresh, mappingContext, onShowDialogue } = useContext(
    PropertyEditorContext
  );
  const objectSelectionOption: ObjectSelectionOptions = getEditorConfigValue(child, 'ObjectSelectionOptions');
  const knownTypes: string[] = getEditorConfigValue(child, 'KnownTypes');
  const typeName: string = getEditorConfigValue(child, 'TypeName');
  const ShowEditDeleteActionsInMenu: boolean = getEditorConfigValue(child, 'ShowEditDeleteActionsInMenu');
  const listItems = useMemo(() => getListItems(child, compareFunction), [child, compareFunction]);

  const openDialogWithSnapshot = useCallback((snapshot: ContextProviderNode | undefined, title: string) => {
    setDialogInfo(preVal => ({
      ...preVal,
      snapshot,
      open: true,
      title,
    }));
  }, []);

  const handleEdit = useCallback(
    async (itemIndex: number) => {
      const snapshot = await editValueAndOpenEditor({
        editingContextId,
        itemIndex,
        path,
      });
      openDialogWithSnapshot(snapshot, `Edit ${child.Node.DisplayLabel}`);
    },
    [child.Node.DisplayLabel, editingContextId, openDialogWithSnapshot, path]
  );

  const addValueAndOpenDialogEditor = useCallback(
    async (itemType?: string) => {
      const snapshot = await addValueAndOpenEditor({
        editingContextId,
        path,
        itemType,
      });
      openDialogWithSnapshot(snapshot, `Add ${child.Node.DisplayLabel}`);
    },
    [child.Node.DisplayLabel, editingContextId, openDialogWithSnapshot, path]
  );

  const addKnownTypesMenu = useCallback(
    (element: Element) => {
      //TODO: If we have menu in React, refactor this
      if (!knownTypes || !knownTypes.length) return;
      const actions: any[] = [];
      knownTypes.forEach(action => {
        actions.push(
          new Decisions.ContextMenuHelper.Action(
            action,
            async () => {
              addValueAndOpenDialogEditor(action);
            },
            actions.length,
            ''
          )
        );
      });

      if (actions.length > 0) {
        Decisions.ContextMenuHelper.showActionsAtRight(actions, element);
      }
    },
    [addValueAndOpenDialogEditor, knownTypes]
  );

  const onAdd = useCallback(
    async (event: React.MouseEvent) => {
      if (knownTypes && knownTypes.length) {
        addKnownTypesMenu(event.currentTarget);
      } else if (objectSelectionOption === ObjectSelectionOptions.DefineOrPick) {
        //TODO:Need to convert this entire control into React. Keeping it in the MVC for now
        $DP.UI.Controls.EntityBuilder.Create(
          editingContextId,
          path.join(','),
          typeName,
          refresh,
          true,
          getListLength(child)
        );
      } else {
        addValueAndOpenDialogEditor();
      }
    },
    [
      knownTypes,
      objectSelectionOption,
      addKnownTypesMenu,
      editingContextId,
      path,
      typeName,
      refresh,
      child,
      addValueAndOpenDialogEditor,
    ]
  );

  const handleCancel = useCallback(() => {
    removeCommand({ contextProviderRegistrationId: editingContextId });
    refresh();
  }, [refresh, editingContextId]);

  const handleDialogAfterOpen = useCallback(() => setBusyIndex(-1), []);
  const handleDialogClose = useCallback(
    () => setDialogInfo(preVal => ({ ...preVal, snapshot: undefined, open: false })),
    []
  );

  const onDrop = useCallback(
    async (index: number) => {
      setBusyIndex(index);
      await removeValue({ contextProviderRegistrationId: editingContextId, index, path });
      setBusyIndex(-1);
    },
    [editingContextId, path]
  );

  // TODO: would like to do drag and drop reordering, but even the MoveValueUp / Down commands would
  // most easily be achieved with an API call from here, but we could fall back on the 'quick action' API for them.
  // if we keep with the move up/down calls, but add a number of steps being moved, we could useReducer to optimistically
  // update here, and then call move up... or we could add a new method to just set the new index of a particular entry.
  // should probably inspect those calls to see how they work.

  const onMoveUp = useCallback(
    async (index: number) => {
      setBusyIndex(index);
      await moveValueUp({ contextProviderRegistrationId: editingContextId, path, index });
      setBusyIndex(-1);
      refreshContext();
    },
    [editingContextId, path, refreshContext]
  );

  const onMoveDown = useCallback(
    async (index: number) => {
      setBusyIndex(index);
      await moveValueDown({ contextProviderRegistrationId: editingContextId, path, index });
      setBusyIndex(-1);
      refreshContext();
    },
    [editingContextId, path, refreshContext]
  );

  const onClearAll = useCallback(() => {
    onChange([], path);
  }, [onChange, path]);

  if (ShowEditDeleteActionsInMenu) {
    actions?.push({
      label: 'Edit',
      onClick: handleEdit,
      value: 'Edit',
    });
    actions?.push({
      label: 'Delete',
      onClick: onDrop,
      value: 'Delete',
    });
  }
  const getListItemsView = useMemo(() => {
    const itemLength = listItems.length;
    return listItems.map((item, i) => (
      <ArrayListItem
        key={`${item.label}-${i}`}
        label={item.label}
        onRemove={disableRemoveAction ? undefined : allowDelete(child) ? onDrop : undefined}
        onEdit={allowEdit(child) ? handleEdit : undefined}
        index={item.index}
        onMoveUp={disableMoveUpDown(child) || i === 0 ? undefined : onMoveUp}
        onMoveDown={disableMoveUpDown(child) || i === itemLength - 1 ? undefined : onMoveDown}
        isActionBusy={busyIndex === item.index ? true : false}
        moreActions={actions}
      />
    ));
  }, [listItems, busyIndex, child, onDrop, handleEdit, onMoveUp, onMoveDown, actions, disableRemoveAction]);

  return (
    <EditorLabel editor={child} path={path} className={getClassNames(busyIndex !== -1)} doNotWrap>
      <EditorValidationWrapper editor={child}>
        <div className={getContentClassNames(listItems, containerClassName)}>
          <div className="dpc-array-editor__list-holder">
            {!!listItems.length && <ul className="dpc-array-editor__list">{getListItemsView}</ul>}
            <ListBoxEmptyMessage text="No Data In List" list={listItems} />
          </div>
          <EditorConfigButtons>
            {allowDelete(child) && !!listItems?.length && (
              <EditorConfigButton onClick={onClearAll}>Clear All</EditorConfigButton>
            )}
            {allowAdd(child) && <EditorConfigButton onClick={onAdd}>Add</EditorConfigButton>}
          </EditorConfigButtons>
        </div>
      </EditorValidationWrapper>
      <PropertyGridDialog
        dialogProps={{
          open: dialogInfo.open,
          styles: { height: DIALOG_SIZE.height, width: DIALOG_SIZE.width },
          keyboard: { autoFocusFirst: true, trapTabbing: true },
          dialogTitle: { titleText: dialogInfo.title },
          onAfterOpen: handleDialogAfterOpen,
          onClose: handleDialogClose,
        }}
        propertyGridProps={{
          snapshot: dialogInfo.snapshot,
          mappingContext,
          onShowDialogue,
        }}
        onSubmit={refresh}
        onCancel={handleCancel}
      />
    </EditorLabel>
  );
}

const getClassNames = (isActionBusy: boolean) => classNames({ 'disabled-overlay': isActionBusy });

const getContentClassNames = (items: IListItemProps[], className?: string) =>
  classNames(className, 'dpc-array-editor', {
    'dpc-array-editor__empty': items?.length === 0,
  });

function getListItems(child: IPropertyChild, comparator?: ListItemComparator): IListItemProps[] {
  comparator = getComparator(child, comparator);
  const itemLabels = (child.Node as ContextListPropertyNode).ItemLabels.filter(item => !isNullOrWhitespace(item));

  const items = itemLabels.map((label, index) => {
    return {
      label,
      index,
    } as IListItemProps;
  });

  return comparator ? items.sort(comparator) : items;
}

function getListLength(child: IPropertyChild): number {
  return (child.Node as ContextListPropertyNode).ItemLabels.length;
}

// helper functions which make the JSX more readable:

function allowAdd(child: IPropertyChild): boolean {
  return !getEditorConfigValue(child, 'DisableAdd');
}

function allowDelete(child: IPropertyChild): boolean {
  return !getEditorConfigValue(child, 'DisableDelete');
}

function allowEdit(child: IPropertyChild): boolean {
  return !getEditorConfigValue(child, 'DisableEdit');
}

function disableMoveUpDown(child: IPropertyChild): boolean {
  return (
    getEditorConfigValue(child, 'DisableMoveUpDown') ||
    sortOrder(child) !== ArraySortOrder.None ||
    getListLength(child) < 2
  );
}

function sortOrder(child: IPropertyChild): ArraySortOrder {
  return getEditorConfigValue(child, 'SortOrder');
}

// function typeName(child: IPropertyChild): string {
//   return getEditorConfigValue(child, 'TypeName');
// }

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function objectSelectionOptions(child: IPropertyChild): number {
  return getEditorConfigValue(child, 'ObjectSelectionOptions');
}

function getComparator(child: IPropertyChild, comparator?: ListItemComparator) {
  if (comparator) {
    // use custom if provided
    return comparator;
  }

  const order = sortOrder(child);
  switch (order) {
    case ArraySortOrder.Ascending:
      return sortAscending;
    case ArraySortOrder.Descending:
      return sortDescending;
    default:
      return;
  }
}

function sortAscending(itemA: IListItemProps, itemB: IListItemProps) {
  const labelA = itemA.label.toUpperCase();
  const labelB = itemB.label.toUpperCase();
  return labelA < labelB ? -1 : labelA > labelB ? 1 : 0;
}

function sortDescending(itemA: IListItemProps, itemB: IListItemProps) {
  const labelA = itemA.label.toUpperCase();
  const labelB = itemB.label.toUpperCase();
  return labelA < labelB ? 1 : labelA > labelB ? -1 : 0;
}
